// fork from https://github.com/dvtng/react-loading-skeleton
import React, {createContext, CSSProperties, PropsWithChildren, ReactElement, useContext} from 'react';
import {asREM, cn} from '../../Utilities';
import scss from './Skeleton.module.scss';

interface SkeletonStyleProps {
    baseColor?: string;
    highlightColor?: string;

    circle?: boolean | number | string;
    width?: string | number;
    height?: string | number;
    borderRadius?: string | number;
    inline?: boolean;

    duration?: number;
    direction?: 'ltr' | 'rtl';
    enableAnimation?: boolean;
}

const defaultEnableAnimation = true;

// For performance & cleanliness, don't add any inline styles unless we have to
function styleOptionsToCssProperties({
    baseColor,
    highlightColor,

    width,
    height,
    borderRadius,
    circle,

    direction,
    duration,
    enableAnimation = defaultEnableAnimation,
}: SkeletonStyleProps): CSSProperties {
    const style: CSSProperties & Record<`--${string}`, string> = {};

    if (direction === 'rtl') style['--animation-direction'] = 'reverse';
    if (typeof duration === 'number') style['--animation-duration'] = `${duration}s`;
    if (!enableAnimation) style['--pseudo-element-display'] = 'none';

    if (typeof borderRadius === 'string') style.borderRadius = borderRadius;
    if (typeof borderRadius === 'number') style.borderRadius = asREM(borderRadius);

    if (circle) style.borderRadius = '50%';
    if (typeof circle !== 'boolean') width = height = circle;

    if (typeof width === 'string') style.width = width;
    if (typeof height === 'string') style.height = height;
    if (typeof width === 'number') style.width = asREM(width);
    if (typeof height === 'number') style.height = asREM(height);

    if (typeof baseColor !== 'undefined') style['--base-color'] = baseColor;
    if (typeof highlightColor !== 'undefined') style['--highlight-color'] = highlightColor;

    return style;
}

function contextToCssProperties({stopped = false}: SkeletonContext = {}) {
    const style: CSSProperties & Record<`--${string}`, string> = {};
    if (stopped) {
        style['--animation-play-state'] = 'paused';
    }
    return style;
}

export interface SkeletonProps extends SkeletonStyleProps {
    count?: number;
    wrapper?: React.FunctionComponent<PropsWithChildren<unknown>>;

    className?: string;
    containerClassName?: string;
    containerTestId?: string;

    style?: CSSProperties;
}

const SkeletonContext = createContext<SkeletonContext>({});
export const SkeletonContextProvider = SkeletonContext.Provider;

type SkeletonContext = Readonly<
    Partial<{
        stopped?: boolean;
    }>
>;

export namespace RsnSkeleton {
    export interface CanShowError {
        gotError: boolean;
    }
    export interface CanResetState {
        reset: () => void;
    }
    export interface CanHandleError extends CanShowError, CanResetState {}
}

export function RsnSkeleton({
    count = 1,
    wrapper: Wrapper,

    className: customClassName,
    containerClassName,
    containerTestId,

    circle = false,

    style: styleProp,
    ...originalPropsStyleOptions
}: SkeletonProps): ReactElement {
    const propsStyleOptions = {...originalPropsStyleOptions};

    // DO NOT overwrite style options from the context if `propsStyleOptions`
    // has properties explicitly set to undefined
    for (const [key, value] of Object.entries(originalPropsStyleOptions)) {
        if (typeof value === 'undefined') {
            delete propsStyleOptions[key as keyof typeof propsStyleOptions];
        }
    }

    // Props take priority over context
    const styleOptions = {
        ...propsStyleOptions,
        circle,
    };

    // `styleProp` has the least priority out of everything
    const style = {
        ...styleProp,
        ...styleOptionsToCssProperties(styleOptions),
        ...contextToCssProperties(useContext(SkeletonContext)),
    };

    let className = scss.skeleton;
    if (customClassName) className += ` ${customClassName}`;

    const inline = styleOptions.inline ?? false;

    const elements: ReactElement[] = [];

    const countCeil = Math.ceil(count);

    for (let i = 0; i < countCeil; i++) {
        let thisStyle = style;

        if (countCeil > count && i === countCeil - 1) {
            // count is not an integer and we've reached the last iteration of
            // the loop, so add a "fractional" skeleton.
            //
            // For example, if count is 3.5, we've already added 3 full
            // skeletons, so now we add one more skeleton that is 0.5 times the
            // original width.

            const width = thisStyle.width ?? '100%'; // 100% is the default since that's what's in the CSS

            const fractionalPart = count % 1;

            const fractionalWidth =
                typeof width === 'number' ? width * fractionalPart : `calc(${width} * ${fractionalPart})`;

            thisStyle = {...thisStyle, width: fractionalWidth};
        }

        const skeletonSpan = (
            <span className={className} style={thisStyle} key={i}>
                &zwnj;
            </span>
        );

        if (inline) {
            elements.push(skeletonSpan);
        } else {
            // Without the <br />, the skeleton lines will all run together if
            // `width` is specified
            elements.push(
                <React.Fragment key={i}>
                    {skeletonSpan}
                    <br />
                </React.Fragment>
            );
        }
    }

    return (
        <span
            className={cn(scss.container, containerClassName)}
            style={style}
            data-testid={containerTestId}
            aria-live='polite'
            aria-busy={styleOptions.enableAnimation ?? defaultEnableAnimation}
        >
            {Wrapper ? elements.map((el, i) => <Wrapper key={i}>{el}</Wrapper>) : elements}
        </span>
    );
}
